import React from 'react';

interface UsageProgressBarProps {
  used: number;
  limit: number;
  label: string;
  unit?: string;
  isUnlimited?: boolean;
  showOverage?: boolean;
  overage?: number;
  compact?: boolean;
}

export const UsageProgressBar: React.FC<UsageProgressBarProps> = ({
  used,
  limit,
  label,
  unit = '',
  isUnlimited = false,
  showOverage = true,
  overage = 0,
  compact = false
}) => {
  const percentUsed = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
  const isInOverage = overage > 0;

  const getBarColor = () => {
    if (isUnlimited) return 'bg-emerald-500';
    if (isInOverage) return 'bg-red-500';
    if (percentUsed >= 90) return 'bg-red-500';
    if (percentUsed >= 75) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getTextColor = () => {
    if (isUnlimited) return 'text-emerald-600';
    if (isInOverage) return 'text-red-600';
    if (percentUsed >= 90) return 'text-red-600';
    if (percentUsed >= 75) return 'text-amber-600';
    return 'text-gray-600';
  };

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  if (compact) {
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">{label}</span>
          <span className={getTextColor()}>
            {isUnlimited ? (
              <span className="text-emerald-600">Unlimited</span>
            ) : (
              <>
                {formatNumber(used)}/{formatNumber(limit)}{unit && ` ${unit}`}
                {isInOverage && showOverage && (
                  <span className="ml-1 text-red-600">(+{formatNumber(overage)})</span>
                )}
              </>
            )}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${getBarColor()}`}
            style={{ width: isUnlimited ? '100%' : `${percentUsed}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className={`text-sm font-semibold ${getTextColor()}`}>
          {isUnlimited ? (
            <span className="text-emerald-600">Unlimited</span>
          ) : (
            <>
              {formatNumber(used)} / {formatNumber(limit)}{unit && ` ${unit}`}
              {isInOverage && showOverage && (
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                  +{formatNumber(overage)} overage
                </span>
              )}
            </>
          )}
        </span>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div
          className={`h-3 rounded-full transition-all duration-500 ${getBarColor()}`}
          style={{ width: isUnlimited ? '100%' : `${percentUsed}%` }}
        />
      </div>

      {!isUnlimited && !compact && (
        <div className="flex justify-between text-xs text-gray-500">
          <span>{Math.round(percentUsed)}% used</span>
          {limit - used > 0 && !isInOverage && (
            <span>{formatNumber(limit - used)}{unit && ` ${unit}`} remaining</span>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageProgressBar;
